// Outdoor field (docs/plans/outdoor-spaces.md, Prototype plan): a square of `size_m` that repeats in x and z (a
// torus). Everything here is pure (no three.js), so the collider, the tests and the scene builder share it:
//  - the manifest's `outdoor` setting, its check and defaults
//  - the ground height: a sum of sines whose wavelengths divide size_m, so it repeats with the field
//  - the placements of the features (ruins of columns, rocks, plants) from the seed, with spacing, and their
//    collision circles
// three.js coordinates, metres; the field covers x and z in [0, size_m).

export interface FieldColors {
  /** Fog and horizon (sRGB hex). */
  fog: string;
  zenith: string;
  ground: string;
  ground2: string;
  stone: string;
  rock: string;
  plant: string;
  /** The speaker box (2026-11-01): oak, the drivers and plugs, the cable, the grey cube. */
  oak: string;
  driver: string;
  cable: string;
  cube: string;
}

/** The hallway's speaker box standing in the field (2026-11-01, user: "place the same speaker from hallway somewhere"),
 *  its cable plugged into a grey cube on the ground. */
export interface SpeakerSpec {
  /** Footprint centre (x, z). */
  position: [number, number];
  /** Direction the front faces: 0 = toward -z, 90 = toward +x. */
  facing_deg: number;
  /** Edge of the grey cube the cable plugs into (m). */
  cube_m: number;
}

export interface FieldSpec {
  /** The field repeats every size_m in x and z. */
  size_m: number;
  seed: number;
  /** Height of the ground's rises and hollows (0 = flat). */
  relief_m: number;
  /** Ground grid spacing, [desktop, mobile]. */
  ground_cell_m: [number, number];
  /** Fog from fog_start_m to view_m; nothing is drawn beyond view_m (must stay under size_m / 2 minus the largest
   *  feature, so a copy never appears or disappears in view). */
  fog_start_m: number;
  view_m: number;
  colors: FieldColors;
  /** Sun: azimuth 0 = toward -z (yaw 0), 90 = toward +x; the direction the light comes from. */
  sun: { azimuth_deg: number; elevation_deg: number; strength: number; ambient: number };
  /** Rocks: count; on mobile only the ones that block the way and this fraction of the others. */
  rocks: { count: number; mobile_small: number };
  plants: { count: number; mobile: number };
  /** Ruin sites: rows of columns, some standing, some broken, some fallen; columns per row [min, max]. */
  ruins: { sites: number; columns: [number, number] };
  /** Stone platforms under the ruins (2026-11-01, user: "broken patterns of an old building's base"): slabs on a
   *  1.6 m grid (half the column grid), the top height_m above a plane fitted to the ground (0 = no platforms);
   *  `missing`: the chance an outer slab is gone (less further in); `steps`: the chance a side has a step. */
  platforms: { height_m: number; missing: number; steps: number };
  /** The speaker box; missing = none. */
  speaker?: SpeakerSpec;
}

export const DEFAULT_FIELD: FieldSpec = {
  size_m: 100,
  seed: 1,
  relief_m: 1.2,
  ground_cell_m: [2, 3],
  fog_start_m: 8,
  view_m: 42,
  colors: { fog: '#e6e1cc', zenith: '#b9c2bd', ground: '#7d8458', ground2: '#a39a6a', stone: '#d8d0bb', rock: '#8a877c', plant: '#5d6b35', oak: '#9b7a4c', driver: '#2b2a27', cable: '#262624', cube: '#6f6f6c' },
  sun: { azimuth_deg: 215, elevation_deg: 34, strength: 0.75, ambient: 0.5 },
  rocks: { count: 520, mobile_small: 0.3 },
  plants: { count: 1800, mobile: 0.4 },
  ruins: { sites: 6, columns: [4, 8] },
  platforms: { height_m: 0, missing: 0.45, steps: 0.5 },
};

const HEX = /^#[0-9a-fA-F]{6}$/;

/** Problems with a manifest `outdoor` setting (empty when valid). Missing fields take the defaults. */
export function fieldProblems(v: unknown): string[] {
  const o = v as Partial<FieldSpec>;
  if (!o || typeof o !== 'object' || Array.isArray(o)) return ['outdoor: not an object'];
  const p: string[] = [];
  const num = (x: unknown, lo: number, hi: number) => typeof x === 'number' && Number.isFinite(x) && x >= lo && x <= hi;
  const s = { ...DEFAULT_FIELD, ...o };
  if (!num(s.size_m, 40, 1000)) p.push('outdoor.size_m: 40..1000');
  if (!(Number.isInteger(s.seed) && num(s.seed, 0, 2 ** 31))) p.push('outdoor.seed: integer');
  if (!num(s.relief_m, 0, 10)) p.push('outdoor.relief_m: 0..10');
  if (!(Array.isArray(s.ground_cell_m) && s.ground_cell_m.length === 2 && s.ground_cell_m.every((c) => num(c, 0.5, 10)))) p.push('outdoor.ground_cell_m: [desktop, mobile] 0.5..10');
  if (!num(s.view_m, 10, 500) || !(s.view_m <= s.size_m / 2 - MAX_FEATURE_R)) p.push(`outdoor.view_m: 10..size_m / 2 - ${MAX_FEATURE_R}`);
  if (!num(s.fog_start_m, 0, 500) || !(s.fog_start_m < s.view_m)) p.push('outdoor.fog_start_m: under view_m');
  if (o.colors !== undefined) {
    if (!o.colors || typeof o.colors !== 'object') p.push('outdoor.colors');
    else for (const [k, c] of Object.entries(o.colors)) if (!(k in DEFAULT_FIELD.colors) || typeof c !== 'string' || !HEX.test(c)) p.push(`outdoor.colors.${k}`);
  }
  if (o.sun !== undefined) {
    const u = { ...DEFAULT_FIELD.sun, ...o.sun };
    if (!num(u.azimuth_deg, -360, 360)) p.push('outdoor.sun.azimuth_deg');
    if (!num(u.elevation_deg, 5, 90)) p.push('outdoor.sun.elevation_deg: 5..90');
    if (!num(u.strength, 0, 4) || !num(u.ambient, 0, 4)) p.push('outdoor.sun.strength / ambient: 0..4');
  }
  if (o.rocks !== undefined) {
    const r = { ...DEFAULT_FIELD.rocks, ...o.rocks };
    if (!(Number.isInteger(r.count) && num(r.count, 0, 20000)) || !num(r.mobile_small, 0, 1)) p.push('outdoor.rocks: count 0..20000, mobile_small 0..1');
  }
  if (o.plants !== undefined) {
    const r = { ...DEFAULT_FIELD.plants, ...o.plants };
    if (!(Number.isInteger(r.count) && num(r.count, 0, 50000)) || !num(r.mobile, 0, 1)) p.push('outdoor.plants: count 0..50000, mobile 0..1');
  }
  if (o.ruins !== undefined) {
    const r = { ...DEFAULT_FIELD.ruins, ...o.ruins };
    if (!(Number.isInteger(r.sites) && num(r.sites, 0, 64))) p.push('outdoor.ruins.sites: 0..64');
    if (!(Array.isArray(r.columns) && r.columns.length === 2 && r.columns.every((c) => Number.isInteger(c) && c >= 1 && c <= 24) && r.columns[1]! >= r.columns[0]!)) p.push('outdoor.ruins.columns: [min, max] 1..24');
  }
  if (o.platforms !== undefined) {
    const r = { ...DEFAULT_FIELD.platforms, ...o.platforms };
    if (!num(r.height_m, 0, 0.6) || !num(r.missing, 0, 1) || !num(r.steps, 0, 1)) p.push('outdoor.platforms: height_m 0..0.6, missing 0..1, steps 0..1');
  }
  if (o.speaker !== undefined) {
    const k = o.speaker;
    if (!k || typeof k !== 'object' || !Array.isArray(k.position) || k.position.length !== 2 || !k.position.every((c) => num(c, 0, s.size_m))
      || !num(k.facing_deg, -360, 360) || !num(k.cube_m, 0.1, 1)) p.push('outdoor.speaker: position [x, z] in the field, facing_deg, cube_m 0.1..1');
  }
  return p;
}

/** The setting with defaults filled in (call fieldProblems first). */
export function fieldSpec(v: Partial<FieldSpec>): FieldSpec {
  const d = DEFAULT_FIELD;
  return {
    ...d, ...v,
    colors: { ...d.colors, ...v.colors },
    sun: { ...d.sun, ...v.sun },
    rocks: { ...d.rocks, ...v.rocks },
    plants: { ...d.plants, ...v.plants },
    ruins: { ...d.ruins, ...v.ruins },
    platforms: { ...d.platforms, ...v.platforms },
  };
}

// ---------------------------------------------------------------- numbers

/** Mulberry32: a small seeded generator, [0, 1). */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** x wrapped into [0, p). */
export const wrap = (x: number, p: number) => ((x % p) + p) % p;
/** A difference wrapped into [-p/2, p/2): the shortest way round the field. */
export const wrapDelta = (d: number, p: number) => d - p * Math.floor(d / p + 0.5);

// ---------------------------------------------------------------- ground height

/** Height terms: whole waves per field in x and z, and a weight (the weights sum to 1). */
const TERMS: [number, number, number][] = [[1, 0, 0.26], [0, 1, 0.22], [1, 1, 0.16], [1, -2, 0.12], [2, 1, 0.1], [3, -1, 0.07], [-2, 3, 0.07]];

export interface Ground {
  period: number;
  /** [kx, kz, amplitude m, phase] per term */
  terms: [number, number, number, number][];
  height(x: number, z: number): number;
  /** dh/dx, dh/dz */
  slope(x: number, z: number): [number, number];
}

export function ground(s: FieldSpec): Ground {
  const r = rng(s.seed ^ 0x9e3779b9);
  const terms = TERMS.map(([kx, kz, w]) => [kx, kz, w * s.relief_m, r() * Math.PI * 2] as [number, number, number, number]);
  const k = (Math.PI * 2) / s.size_m;
  return {
    period: s.size_m,
    terms,
    height(x, z) {
      let h = 0;
      for (const [kx, kz, a, ph] of terms) h += a * Math.sin(k * (kx * x + kz * z) + ph);
      return h;
    },
    slope(x, z) {
      let gx = 0, gz = 0;
      for (const [kx, kz, a, ph] of terms) {
        const c = a * k * Math.cos(k * (kx * x + kz * z) + ph);
        gx += c * kx; gz += c * kz;
      }
      return [gx, gz];
    },
  };
}

/** The same height and slope in GLSL (`fieldHeight(vec2 xz)`, `fieldSlope(vec2 xz)`), constants from `g`. */
export function groundGlsl(g: Ground): string {
  const k = ((Math.PI * 2) / g.period).toFixed(8);
  const f = (x: number) => x.toFixed(6);
  const hs = g.terms.map(([kx, kz, a, ph]) => `h += ${f(a)} * sin( ${k} * ( ${f(kx)} * p.x + ${f(kz)} * p.y ) + ${f(ph)} );`).join('\n  ');
  const gs = g.terms.map(([kx, kz, a, ph]) => `c = ${f(a * ((Math.PI * 2) / g.period))} * cos( ${k} * ( ${f(kx)} * p.x + ${f(kz)} * p.y ) + ${f(ph)} ); s += c * vec2( ${f(kx)}, ${f(kz)} );`).join('\n  ');
  return `float fieldHeight( vec2 p ) {\n  float h = 0.0;\n  ${hs}\n  return h;\n}\nvec2 fieldSlope( vec2 p ) {\n  vec2 s = vec2( 0.0 ); float c;\n  ${gs}\n  return s;\n}\n`;
}

/** A smooth value in [0, 1] that repeats with the field (patches of plants and rocks). */
function patches(s: FieldSpec, salt: number): (x: number, z: number) => number {
  const r = rng(s.seed ^ salt);
  const t = [[2, 1], [1, -3], [3, 2], [-4, 1]].map(([a, b]) => [a!, b!, r() * Math.PI * 2]);
  const k = (Math.PI * 2) / s.size_m;
  return (x, z) => {
    let v = 0;
    for (const [a, b, ph] of t) v += Math.sin(k * (a! * x + b! * z) + ph!);
    return 0.5 + v / 8;
  };
}

// ---------------------------------------------------------------- features

export type FeatureKind = 'rock' | 'plant' | 'column' | 'piece' | 'slab';

/** Unit sizes of the generated meshes (field-scene.ts builds them to these). */
export const COLUMN = { shaft_r: 0.36, height: 5.4, plinth: 0.95 };
/** A column drum or shaft piece: a fluted cylinder of radius 1 and height 1 along +y, scaled per instance. */
export const PIECE_R = 0.34;
/** The largest feature's reach from its origin (a fallen shaft's half length plus margin); view_m must leave room. */
export const MAX_FEATURE_R = 6;

/** One feature: kind, origin (on the ground), rotation (quaternion x, y, z, w), scale, tint multiplier. */
export interface Placement {
  kind: FeatureKind;
  p: [number, number, number];
  q: [number, number, number, number];
  s: [number, number, number];
  tint: number;
  /** Shown on the mobile tier too (every feature that blocks the way is, so collision is the same on every tier). */
  mobile: boolean;
}

/** A collision circle on the ground: x, z, radius. */
export type Circle = [number, number, number];

/** A platform slab's top: centre (x, y, z), half sizes along its own axes, yaw (rad, as qYaw), and the top's
 *  gradient (dy/dx, dy/dz in world axes), so the top at (x + dx, z + dz) is y + gx dx + gz dz. */
export interface Slab { x: number; y: number; z: number; hx: number; hz: number; yaw: number; gx: number; gz: number }

/** The speaker box in the field (local frame: +x to the right seen from the front, +y up, +z the front). */
export interface SpeakerBox {
  /** Footprint centre at the box's bottom (it stands level; the bottom is sunk to the lowest corner's ground). */
  origin: [number, number, number];
  /** Rotation about +y (rad) taking the local frame to the world (local +z to `forward`). */
  yaw: number;
  forward: [number, number, number];
  right: [number, number, number];
  /** The music sources: one per speaker column at its woofer centre on the front face, as the hallway's. */
  sources: { id: string; position: [number, number, number]; forward: [number, number, number] }[];
  /** The rear bass port's opening (back face centre, 1.1 m up), world. */
  port: [number, number, number];
  /** The grey cube: centre of its bottom face (local) and edge. */
  cube: { at: [number, number, number]; m: number };
  /** The cable's centre line (local), from the plug in the box's back to the plug in the cube, and its radius. */
  cable: [number, number, number][];
  cable_r: number;
  /** Collision: the box as a row of circles, and the cube (the cable does not block). */
  circles: Circle[];
}

/** The speaker box's sizes (m; cad projects/objects/speaker_box: W 1800, D 800, H 1700; drivers per column: woofer
 *  r 300 at 600, mid r 140 at 1130, tweeter r 34 at 1450, columns at x -/+450; port r 65 at the back centre 1100 up). */
export const SPEAKER = {
  w: 1.8, d: 0.8, h: 1.7,
  columns: [-0.45, 0.45],
  drivers: [[0.6, 0.3], [1.13, 0.14], [1.45, 0.034]] as [number, number][],
  port: { y: 1.1, r: 0.065 },
  /** The cable's socket on the back (local x, y). cad has it at x -500; here on the +x side, toward the cube. */
  socket: [0.5, 0.42] as [number, number],
  /** 40 mm across (cad 32 mm): reads at 360 px. */
  cable_r: 0.02,
};

export interface FieldLayout {
  placements: Placement[];
  circles: Circle[];
  /** Ruin site centres (x, z). */
  sites: [number, number][];
  /** Platform slabs (also in placements as kind 'slab'), for the floor height. */
  slabs: Slab[];
  speaker: SpeakerBox | null;
}

type Q = [number, number, number, number];
const qYaw = (a: number): Q => [0, Math.sin(a / 2), 0, Math.cos(a / 2)];
const qAxis = (x: number, y: number, z: number, a: number): Q => { const s = Math.sin(a / 2); return [x * s, y * s, z * s, Math.cos(a / 2)]; };
const qMul = (a: Q, b: Q): Q => [
  a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
  a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
  a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
  a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
];
/** Rotation taking +y to the unit vector d. */
function qFromY(dx: number, dy: number, dz: number): Q {
  // axis = y x d = (dz, 0, -dx); angle = acos(dy)
  const l = Math.hypot(dz, dx);
  if (l < 1e-9) return dy > 0 ? [0, 0, 0, 1] : [1, 0, 0, 0];
  return qAxis(dz / l, 0, -dx / l, Math.acos(Math.max(-1, Math.min(1, dy))));
}

/** Circles in a grid of cells on the torus: is a new circle clear of all the others? */
class TorusGrid {
  private readonly n: number;
  private readonly cell: number;
  private readonly cells: Circle[][];
  constructor(readonly period: number, cell: number) {
    this.n = Math.max(1, Math.floor(period / cell));
    this.cell = period / this.n;
    this.cells = Array.from({ length: this.n * this.n }, () => []);
  }
  private idx(i: number, j: number) {
    const n = this.n;
    return (((i % n) + n) % n) * n + (((j % n) + n) % n);
  }
  add(c: Circle): void {
    const r = c[2], cs = this.cell;
    for (let i = Math.floor((c[0] - r) / cs); i <= Math.floor((c[0] + r) / cs); i++)
      for (let j = Math.floor((c[1] - r) / cs); j <= Math.floor((c[1] + r) / cs); j++) {
        const l = this.cells[this.idx(i, j)]!;
        if (!l.includes(c)) l.push(c);
      }
  }
  /** No circle closer than `r` + its own radius to (x, z); `reach` = the largest radius in the grid. */
  clear(x: number, z: number, r: number, reach: number): boolean {
    const cs = this.cell, p = this.period, R = r + reach;
    for (let i = Math.floor((x - R) / cs); i <= Math.floor((x + R) / cs); i++)
      for (let j = Math.floor((z - R) / cs); j <= Math.floor((z + R) / cs); j++)
        for (const c of this.cells[this.idx(i, j)]!) {
          const dx = wrapDelta(x - c[0], p), dz = wrapDelta(z - c[1], p);
          if (dx * dx + dz * dz < (r + c[2]) ** 2) return false;
        }
    return true;
  }
}

type V3 = [number, number, number];

/** Uniform Catmull-Rom through `pts` (any dimension), `per` samples per segment, ends included. */
function catmullRom(pts: number[][], per: number): number[][] {
  const out: number[][] = [];
  const n = pts.length;
  for (let i = 0; i < n - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)]!, p1 = pts[i]!, p2 = pts[i + 1]!, p3 = pts[Math.min(n - 1, i + 2)]!;
    for (let k = 0; k < per || (i === n - 2 && k === per); k++) {
      const t = k / per, t2 = t * t, t3 = t2 * t;
      out.push(p1.map((_, d) => 0.5 * (2 * p1[d]! + (-p0[d]! + p2[d]!) * t + (2 * p0[d]! - 5 * p1[d]! + 4 * p2[d]! - p3[d]!) * t2 + (-p0[d]! + 3 * p1[d]! - 3 * p2[d]! + p3[d]!) * t3)));
    }
  }
  return out;
}

/**
 * The speaker box at `k`: standing level on the ground, the music sources at the woofers, the port, the grey cube
 * beside it and the cable from the box's back (a loop on the ground) to the cube.
 */
export function speakerBox(k: SpeakerSpec, g: Ground): SpeakerBox {
  const a = (k.facing_deg * Math.PI) / 180;
  const f: V3 = [Math.sin(a), 0, -Math.cos(a)], r: V3 = [-Math.cos(a), 0, -Math.sin(a)];
  const [ox, oz] = k.position;
  const W = (lx: number, lz: number): [number, number] => [ox + r[0] * lx + f[0] * lz, oz + r[2] * lx + f[2] * lz];
  const hw = SPEAKER.w / 2, hd = SPEAKER.d / 2;
  const oy = Math.min(...[[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]].map(([u, v]) => g.height(...W(u!, v!)))) - 0.02;
  const world = (lx: number, ly: number, lz: number): V3 => { const [x, z] = W(lx, lz); return [x, oy + ly, z]; };
  const groundLocal = (lx: number, lz: number) => g.height(...W(lx, lz)) - oy;
  const sources = SPEAKER.columns.map((cx, i) => ({ id: i ? 'box_right' : 'box_left', position: world(cx, SPEAKER.drivers[0]![0], hd), forward: f }));
  // the cube: beside the box on the socket's side, a little behind its back
  const c = k.cube_m, cubeX = hw + 1.4, cubeZ = -0.95;
  const cubeY = groundLocal(cubeX, cubeZ) - 0.01;
  const R = SPEAKER.cable_r;
  const end = cubeX - c / 2 - 0.07; // the plug sticks 70 mm out of the cube's side
  const plugY = cubeY + c / 2;
  const [sx, sy] = SPEAKER.socket;
  // control points: local x, z, on the ground (1) or in the air (0), height in the air
  const G = 1, A = 0;
  const ctrl: number[][] = [
    [sx, -hd - 0.09, A, sy], [sx, -hd - 0.2, A, sy - 0.06], [sx + 0.05, -hd - 0.42, G, 0], [sx + 0.3, -hd - 0.78, G, 0],
    [sx + 0.7, -hd - 0.94, G, 0], [sx + 0.97, -hd - 0.66, G, 0], [sx + 0.77, -hd - 0.36, G, 0], [sx + 0.46, -hd - 0.5, G, 0],
    [sx + 0.54, -hd - 0.87, G, 0], [sx + 0.92, -hd - 1.08, G, 0], [sx + 1.3, -hd - 0.8, G, 0], [end - 0.3, cubeZ, G, 0],
    [end - 0.09, cubeZ, A, plugY * 0.6], [end, cubeZ, A, plugY],
  ];
  const line = catmullRom(ctrl, 8).map(([x, z, w, ay]) => {
    const t = Math.max(0, Math.min(1, w!));
    return [x!, (1 - t) * ay! + t * (groundLocal(x!, z!) + R), z!] as V3;
  });
  // where the cable crosses itself, the later part lies over the earlier one
  const arc = [0];
  for (let i = 1; i < line.length; i++) arc.push(arc[i - 1]! + Math.hypot(line[i]![0] - line[i - 1]![0], line[i]![2] - line[i - 1]![2]));
  let lift = line.map((p, i) => {
    let l = 0;
    for (let j = 0; j < i; j++) {
      if (arc[i]! - arc[j]! < 0.5) break;
      const d = Math.hypot(p[0] - line[j]![0], p[2] - line[j]![2]);
      l = Math.max(l, 2.2 * R * Math.max(0, Math.min(1, 1.6 - d / (2 * R))));
    }
    return l;
  });
  for (let pass = 0; pass < 3; pass++) lift = lift.map((v, i) => Math.max(v, 0.25 * (lift[i - 1] ?? v) + 0.5 * v + 0.25 * (lift[i + 1] ?? v)));
  line.forEach((p, i) => { p[1] += lift[i]!; });
  const circles: Circle[] = [-0.6, -0.2, 0.2, 0.6].map((u) => { const [x, z] = W(u, 0); return [x, z, 0.42] as Circle; });
  const [qx, qz] = W(cubeX, cubeZ);
  circles.push([qx, qz, c * 0.72]);
  return {
    origin: [ox, oy, oz], yaw: Math.PI - a, forward: f, right: r, sources,
    port: world(0, SPEAKER.port.y, -hd), cube: { at: [cubeX, cubeY, cubeZ], m: c }, cable: line, cable_r: R, circles,
  };
}

/** The highest slab top over (x, z), or -Infinity (no slab there). Linear in the slabs: for building the layout. */
export function slabTop(slabs: readonly Slab[], x: number, z: number, P: number): number {
  let best = -Infinity;
  for (const b of slabs) {
    const dx = wrapDelta(x - b.x, P), dz = wrapDelta(z - b.z, P);
    if (Math.abs(dx) > b.hx + b.hz || Math.abs(dz) > b.hx + b.hz) continue;
    const c = Math.cos(b.yaw), sn = Math.sin(b.yaw);
    if (Math.abs(dx * c - dz * sn) > b.hx || Math.abs(dx * sn + dz * c) > b.hz) continue;
    best = Math.max(best, b.y + b.gx * dx + b.gz * dz);
  }
  return best;
}

/** The walking height: the ground, or a slab's top where there is one (slabs in a grid of cells on the torus). */
export class FieldFloor {
  private readonly n: number;
  private readonly cell: number;
  private readonly cells: Slab[][];
  constructor(slabs: readonly Slab[], private readonly g: Ground, private readonly period: number) {
    this.n = Math.max(1, Math.floor(period / 4));
    this.cell = period / this.n;
    this.cells = Array.from({ length: this.n * this.n }, () => []);
    for (const b of slabs) {
      const r = Math.hypot(b.hx, b.hz), cs = this.cell;
      for (let i = Math.floor((b.x - r) / cs); i <= Math.floor((b.x + r) / cs); i++)
        for (let j = Math.floor((b.z - r) / cs); j <= Math.floor((b.z + r) / cs); j++) this.cells[this.idx(i, j)]!.push(b);
    }
  }
  private idx(i: number, j: number) {
    const n = this.n;
    return (((i % n) + n) % n) * n + (((j % n) + n) % n);
  }
  height(x: number, z: number): number {
    const l = this.cells[this.idx(Math.floor(wrap(x, this.period) / this.cell), Math.floor(wrap(z, this.period) / this.cell))]!;
    return Math.max(this.g.height(x, z), l.length ? slabTop(l, x, z, this.period) : -Infinity);
  }
}

/**
 * Every feature of the field from its seed. Deterministic: the same spec gives the same layout on every client
 * (the multiplayer view and the collision agree). Order: ruin sites, their columns and pieces; rocks; plants.
 */
export function layout(s: FieldSpec): FieldLayout {
  const P = s.size_m;
  const g = ground(s);
  const out: Placement[] = [];
  const circles: Circle[] = [];
  /** Keep-out circles for spacing (bigger than the collision circles for rocks; plants keep a small one). */
  const keep = new TorusGrid(P, 4);
  let reach = 0;
  const keepOut = (c: Circle) => { keep.add(c); reach = Math.max(reach, c[2]); };
  const block = (c: Circle) => { const w: Circle = [wrap(c[0], P), wrap(c[1], P), c[2]]; circles.push(w); keepOut(w); };
  const put = (kind: FeatureKind, x: number, z: number, y: number, q: Q, sc: [number, number, number], tint: number, mobile: boolean) =>
    out.push({ kind, p: [wrap(x, P), y, wrap(z, P)], q, s: sc, tint, mobile });

  // ---- ruin sites: the first in the middle (seen from the start point), the others spread out
  const R = rng(s.seed ^ 0x51ed);
  const sites: [number, number][] = [];
  for (let k = 0, tries = 0; k < s.ruins.sites && tries < 500; tries++) {
    const c: [number, number] = k === 0 ? [P / 2, P / 2] : [R() * P, R() * P];
    if (sites.some(([x, z]) => Math.hypot(wrapDelta(x - c[0], P), wrapDelta(z - c[1], P)) < Math.min(34, P / 2.5))) continue;
    sites.push(c);
    k++;
  }
  // ---- platforms under the ruins: their slabs, and the walking height over them
  const slabs: Slab[] = [];
  const RS = rng(s.seed ^ 0x5ab5); // its own sequence: the columns stay as they were without platforms
  const rects: [number, number, number, number][] = [];
  const floorAt = (x: number, z: number) => Math.max(g.height(x, z), slabTop(slabs, x, z, P));
  const platform = (sx: number, sz: number, nx: number, nz: number, gap: number) => {
    const H = s.platforms.height_m;
    if (H <= 0) return;
    const st = gap / 2, joint = 0.03, h = st / 2 - joint / 2;
    const I = 2 * (nx - 1) + 3, J = 2 * (nz - 1) + 3;
    const cx = (i: number) => sx + (i - (I - 1) / 2) * st, cz = (j: number) => sz + (j - (J - 1) / 2) * st;
    // the top: a plane fitted to the ground under the slabs (least squares; the grid is symmetric), height_m above
    let a = 0, bx = 0, bz = 0, sxx = 0, szz = 0;
    for (let i = 0; i < I; i++) for (let j = 0; j < J; j++) {
      const dx = cx(i) - sx, dz = cz(j) - sz, y = g.height(cx(i), cz(j));
      a += y; bx += dx * y; bz += dz * y; sxx += dx * dx; szz += dz * dz;
    }
    a /= I * J; bx /= sxx || 1; bz /= szz || 1;
    const top = (x: number, z: number) => a + bx * (x - sx) + bz * (z - sz) + H;
    rects.push([sx, sz, (I / 2) * st + 0.6, (J / 2) * st + 0.6]);
    /** One slab, centre (x, z), half sizes; `flat`: under a column (not sunk, tilted or turned); `drop`: below the top. */
    const slab = (x: number, z: number, hx: number, hz: number, flat: boolean, drop: number): boolean => {
      const sink = flat ? 0 : RS() < 0.35 ? -(0.02 + RS() * 0.07) : 0;
      const t = flat ? 0 : RS() < 0.3 ? RS() * 0.035 : 0, ph = RS() * Math.PI * 2;
      const yaw = flat ? 0 : (RS() - 0.5) * 0.05;
      const tint = 0.9 + RS() * 0.16;
      const gx = bx + t * Math.cos(ph), gz = bz + t * Math.sin(ph);
      const y = top(x, z) - drop + sink;
      if (y - g.height(x, z) < 0.05) return false; // buried
      const low = Math.min(g.height(x - hx, z - hz), g.height(x + hx, z - hz), g.height(x - hx, z + hz), g.height(x + hx, z + hz));
      const n = Math.hypot(gx, 1, gz);
      put('slab', x, z, y, qMul(qFromY(-gx / n, 1 / n, -gz / n), qYaw(yaw)), [2 * hx, y - low + 0.3, 2 * hz], tint, true);
      slabs.push({ x: wrap(x, P), y, z: wrap(z, P), hx, hz, yaw, gx, gz });
      return true;
    };
    const ring = (i: number, j: number) => Math.min(i, I - 1 - i, j, J - 1 - j);
    const isCol = (i: number, j: number) => i % 2 === 1 && j % 2 === 1 && (i === 1 || i === I - 2 || j === 1 || j === J - 2);
    const present = new Set<number>();
    for (let i = 0; i < I; i++) for (let j = 0; j < J; j++) {
      const flat = isCol(i, j);
      if (!flat && RS() < s.platforms.missing * [1, 0.4, 0.12][Math.min(ring(i, j), 2)]!) continue; // gone: more at the edge
      const x = cx(i), z = cz(j);
      let ok: boolean;
      if (!flat && RS() < 0.15) {
        // split in two, a crack between
        const c = 0.04, hp = (2 * h - c) / 4, o = hp + c / 2;
        ok = RS() < 0.5
          ? [slab(x - o, z, hp, h, false, 0), slab(x + o, z, hp, h, false, 0)].some(Boolean)
          : [slab(x, z - o, h, hp, false, 0), slab(x, z + o, h, hp, false, 0)].some(Boolean);
      } else ok = slab(x, z, h, h, flat, 0);
      if (ok) present.add(i * J + j);
    }
    // a step along some sides, outside the outer slabs that are there
    const sd = 0.25;
    for (const side of [0, 1, 2, 3]) {
      if (RS() >= s.platforms.steps) continue;
      const along = side < 2 ? J : I;
      for (let k = 0; k < along; k++) {
        const [i, j] = side === 0 ? [0, k] : side === 1 ? [I - 1, k] : side === 2 ? [k, 0] : [k, J - 1];
        if (!present.has(i * J + j) || RS() >= 0.8) continue;
        const x = side === 0 ? cx(0) - st / 2 - sd : side === 1 ? cx(I - 1) + st / 2 + sd : cx(i);
        const z = side === 2 ? cz(0) - st / 2 - sd : side === 3 ? cz(J - 1) + st / 2 + sd : cz(j);
        const dh = top(x, z) - g.height(x, z);
        if (dh < 0.22) continue;
        slab(x, z, side < 2 ? sd - joint / 2 : h, side < 2 ? h : sd - joint / 2, false, Math.min(0.17, dh / 2));
      }
    }
  };
  /** A lying piece (drum or shaft) of length L along the horizontal direction a, its middle at (x, z). */
  const lying = (x: number, z: number, a: number, L: number, tint: number) => {
    const dx = Math.cos(a), dz = Math.sin(a);
    const x0 = x - (dx * L) / 2, z0 = z - (dz * L) / 2, x1 = x + (dx * L) / 2, z1 = z + (dz * L) / 2;
    const h0 = floorAt(x0, z0), h1 = floorAt(x1, z1);
    // the axis follows the ground between the two ends; the piece's origin is its bottom end's centre
    const dy = h1 - h0, len = Math.hypot(L, dy);
    const q = qMul(qFromY(dx * (L / len), dy / len, dz * (L / len)), qYaw(R() * Math.PI * 2));
    const sink = PIECE_R * 0.12;
    put('piece', x0, z0, h0 + PIECE_R - sink, q, [PIECE_R, len, PIECE_R], tint, true);
    for (let t = 0; t <= L + 1e-6; t += Math.max(0.3, Math.min(0.5, L / 2))) block([x0 + dx * t, z0 + dz * t, PIECE_R + 0.04]);
  };
  // Each site is a building's colonnade (user 2026-11-01: the columns aligned as parts of buildings): the columns
  // on the outline of a rectangle on a 3.2 m grid, every site on the same axes (x, z), standing columns and stumps
  // upright and turned alike. Fallen pieces lie outward from the building.
  for (const [sx, sz] of sites) {
    const n = s.ruins.columns[0] + Math.floor(R() * (s.ruins.columns[1] - s.ruins.columns[0] + 1));
    const m = 2 + Math.floor(R() * 3); // columns along z: 2 (a portico) to 4
    const along = R() < 0.5; // the long side along x or z
    const [nx, nz] = along ? [n, m] : [m, n];
    const gap = 3.2;
    platform(sx, sz, nx, nz, gap);
    for (let i = 0; i < nx; i++) {
      for (let j = 0; j < nz; j++) {
        if (i > 0 && i < nx - 1 && j > 0 && j < nz - 1) continue; // the outline only
        const x = sx + (i - (nx - 1) / 2) * gap, z = sz + (j - (nz - 1) / 2) * gap;
        const y = floorAt(x, z);
        const tint = 0.9 + R() * 0.16;
        const state = R();
        if (state < 0.45) {
          // standing, upright
          put('column', x, z, y - 0.05, [0, 0, 0, 1], [1, 1, 1], tint, true);
          block([x, z, COLUMN.plinth * 0.55]);
        } else if (state < 0.7) {
          // broken: a stump of the shaft, upright
          const L = 0.6 + R() * 2.4;
          put('piece', x, z, y - 0.1, [0, 0, 0, 1], [PIECE_R * 1.05, L, PIECE_R * 1.05], tint, true);
          block([x, z, PIECE_R + 0.06]);
        } else {
          // fallen: a shaft or a row of drums lying outward from the building, from near the base
          const fa = Math.atan2(z - sz, x - sx) + (R() - 0.5) * 0.8;
          const off = 0.8;
          if (R() < 0.6) {
            const L = 2 + R() * 2.6;
            lying(x + Math.cos(fa) * (off + L / 2), z + Math.sin(fa) * (off + L / 2), fa, L, tint);
          } else {
            const k = 2 + Math.floor(R() * 2);
            for (let d = 0; d < k; d++) {
              const c = off + 0.5 + d * (0.9 + R() * 0.5);
              lying(x + Math.cos(fa) * c, z + Math.sin(fa) * c, fa + (R() - 0.5) * 0.5, 0.7 + R() * 0.2, tint);
            }
          }
        }
      }
    }
    // loose drums round the site, lying
    const extra = 2 + Math.floor(R() * 4);
    for (let e = 0; e < extra; e++) {
      const d = 3 + R() * 6, b = R() * Math.PI * 2;
      const x = sx + Math.cos(b) * d, z = sz + Math.sin(b) * d;
      if (!keep.clear(wrap(x, P), wrap(z, P), 0.6, reach)) continue;
      const tint = 0.9 + R() * 0.16;
      // lying only: an upright drum off the building's grid would read as a column out of line
      lying(x, z, R() * Math.PI, 0.7 + R() * 0.25, tint);
    }
  }

  // ---- the speaker box (hand placed), after the ruins (their layout stays as without it), before the rocks,
  // which keep clear of it and its cable
  const speaker = s.speaker ? speakerBox(s.speaker, g) : null;
  if (speaker) {
    for (const c of speaker.circles) block(c);
    const [ox, , oz] = speaker.origin, [rx, , rz] = speaker.right, [fx, , fz] = speaker.forward;
    keepOut([ox, oz, 1.4]);
    for (let i = 0; i < speaker.cable.length; i += 4) {
      const [lx, , lz] = speaker.cable[i]!;
      keepOut([wrap(ox + rx * lx + fx * lz, P), wrap(oz + rz * lx + fz * lz, P), 0.35]);
    }
  }
  // ---- rocks: dart throwing with spacing, more in some patches; the big ones block the way
  const RR = rng(s.seed ^ 0x70c5);
  const rockPatch = patches(s, 0x1234);
  for (let k = 0, tries = 0; k < s.rocks.count && tries < s.rocks.count * 30; tries++) {
    const x = RR() * P, z = RR() * P;
    const big = RR();
    const sc = 0.18 + big * big * big * 1.3; // mostly small
    if (RR() > 0.25 + 0.75 * rockPatch(x, z)) continue;
    if (rects.some(([cx, cz, hx, hz]) => Math.abs(wrapDelta(x - cx, P)) < hx + sc && Math.abs(wrapDelta(z - cz, P)) < hz + sc)) continue; // not on a platform
    if (!keep.clear(x, z, sc * 0.9 + 0.5, reach)) continue;
    const sx = sc * (0.8 + RR() * 0.5), sy = sc * (0.45 + RR() * 0.45), sz = sc * (0.8 + RR() * 0.5);
    const ta = RR() * Math.PI * 2;
    const tilt = qAxis(Math.cos(ta), 0, Math.sin(ta), RR() * 0.25);
    const blocks = sc > 0.55;
    put('rock', x, z, g.height(x, z) - sy * 0.25, qMul(tilt, qYaw(RR() * Math.PI * 2)), [sx, sy, sz], 0.85 + RR() * 0.3, blocks || RR() < s.rocks.mobile_small);
    if (blocks) block([x, z, Math.max(sx, sz) * 0.85]);
    else keepOut([x, z, sc * 0.6]);
    k++;
  }

  // ---- plants: tufts in patches, clear of stones and of each other
  const RP = rng(s.seed ^ 0x9a17);
  const plantPatch = patches(s, 0x4321);
  const plants = new TorusGrid(P, 2);
  let plantReach = 0;
  for (let k = 0, tries = 0; k < s.plants.count && tries < s.plants.count * 30; tries++) {
    const x = RP() * P, z = RP() * P;
    const v = plantPatch(x, z);
    if (RP() > v * v * 1.6) continue;
    const sc = 0.6 + RP() * 0.8;
    if (!keep.clear(x, z, 0.15, reach) || !plants.clear(x, z, 0.3 * sc, plantReach)) continue;
    if (rects.some(([cx, cz, hx, hz]) => Math.abs(wrapDelta(x - cx, P)) < hx && Math.abs(wrapDelta(z - cz, P)) < hz)) continue;
    plants.add([x, z, 0.3 * sc]);
    plantReach = Math.max(plantReach, 0.3 * sc);
    put('plant', x, z, g.height(x, z) - 0.02, qYaw(RP() * Math.PI * 2), [sc, sc * (0.7 + RP() * 0.6), sc], 0.8 + RP() * 0.4, RP() < s.plants.mobile);
    k++;
  }
  return { placements: out, circles, sites, slabs, speaker };
}

const memo = new Map<string, FieldLayout>();
/** layout(), once per spec (the player's collider and the scene builder both ask). */
export function layoutOnce(s: FieldSpec): FieldLayout {
  const key = JSON.stringify(s);
  let l = memo.get(key);
  if (!l) {
    l = layout(s);
    memo.clear();
    memo.set(key, l);
  }
  return l;
}
